import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {forgetOnboardingStatus} from '../../utils/onboardingProgress';

/**
 * Las llamadas del túnel de alta, todas en un lugar.
 *
 * Cada una devuelve el objeto crudo del backend: la interpretación vive en los módulos puros
 * (`wizardPayload`, `wizardReducer`), que se pueden testear sin red. `APIClient` nunca rechaza, así
 * que el caller siempre recibe un objeto y decide qué hacer con `status`.
 */

export function fetchCandidates(channels) {
    const query = channels && channels.length ? `?channels=${encodeURIComponent(channels.join(','))}` : '';
    return new APIClient(`${API_URL}agency/global-lines/candidates/${query}`).call('GET');
}

export function fetchClientProposals() {
    return new APIClient(`${API_URL}agency/onboarding/client-proposals/`).call('GET');
}

/**
 * Pide en segundo plano el gasto de los últimos 7 días de las cuentas sin cliente (C-02, contrato §1.2).
 *
 * Una agencia nueva llega al paso 3 sin foto de gasto (una cuenta sin línea no entra al sync), así que
 * sin esto el paso no sabría qué cuentas invirtieron. Es idempotente del lado del backend; con un
 * backend viejo da 404 y el paso sigue como siempre. Responde `{preparing: N}`.
 */
export function prepareRecentSpend() {
    return new APIClient(`${API_URL}agency/onboarding/prepare-recent-spend/`).call('POST', {});
}

export function fetchSuggestedBudgets(adAccountPks) {
    if (!adAccountPks || !adAccountPks.length) return Promise.resolve({status: 'OK', data: {budgets: {}}});
    // El backend corta en 200 ids sin avisar, así que se pide de a tandas y se junta acá: una
    // agencia con 400 cuentas vería la mitad de sus presupuestos en blanco sin ningún error.
    const batches = chunk(adAccountPks, 200);
    return Promise.all(batches.map(ids =>
        new APIClient(`${API_URL}agency/ad-accounts/suggested-budgets/?ids=${ids.join(',')}`).call('GET')
    )).then(mergeBudgetResponses);
}

/**
 * Pedirle a las plataformas el catálogo de campañas de estas cuentas.
 *
 * Es lo que habilita la segunda fuente del presupuesto sugerido (el diario configurado en la
 * plataforma). El catálogo **no se trae al conectar la cuenta** —el único disparo automático aborta
 * porque la cuenta todavía no está vinculada a ningún cliente, que es exactamente la situación del
 * túnel—, así que sin esta llamada la fuente `PLATFORM_BUDGET` no podía existir nunca y la mayoría
 * de las filas del paso 4 nacía sin sugerencia.
 *
 * Es un POST aparte y no un efecto del GET a propósito: traer el catálogo son N llamadas a la API de
 * la plataforma, y si lo disparara el GET cada vuelta del poll volvería a encolarlas. Se llama UNA
 * vez, con las cuentas que la pantalla va a mostrar.
 */
export function prepareBudgets(adAccountPks) {
    if (!adAccountPks || !adAccountPks.length) return Promise.resolve({status: 'OK'});
    // Por tandas, igual que el GET de acá arriba y por el mismo tope de 200 del backend. Antes
    // **descartaba** todo lo que pasara de 200 (`slice(0, 200)`), así que en una cartera grande el
    // catálogo no se pedía nunca para la cuenta 201 en adelante y esas líneas nacían sin
    // presupuesto — sin ningún aviso, y justo en la agencia para la que el alta masiva existe.
    //
    // Fail-soft a propósito: si una tanda se cae, las otras igual encolan su catálogo. El caller
    // no mira esta respuesta (sólo espera para arrancar el poll), y la falta de monto ya la cuenta
    // la pantalla con su progreso.
    const batches = chunk(adAccountPks, 200);
    return Promise.all(batches.map(ids =>
        new APIClient(`${API_URL}agency/onboarding/prepare-budgets/`).call('POST', {ids})
    )).then(responses => ({
        status: responses.some(response => response?.status === 'OK') ? 'OK' : 'ERROR',
    }));
}

// Cada cuenta es una llamada del backend a Meta: el backend corta en 25 por pedido.
export const CONVERSION_EVENTS_BATCH = 25;

/**
 * Los eventos que registró cada cuenta de Meta en los últimos 30 días, con su volumen. UNA tanda:
 * quien llama recorre la cartera de a una, para que cada tanda se pinte apenas llega y no haya
 * decenas de pedidos simultáneos contra Meta.
 */
export function fetchConversionEvents(adAccountPks) {
    if (!adAccountPks || !adAccountPks.length) return Promise.resolve({status: 'OK', data: {events: {}}});
    return new APIClient(`${API_URL}agency/onboarding/conversion-events/?ids=${adAccountPks.join(',')}`).call('GET');
}

export function conversionEventsBatches(adAccountPks) {
    return chunk(adAccountPks || [], CONVERSION_EVENTS_BATCH);
}

/**
 * Las acciones de conversión de UNA cuenta (C-01): el mismo endpoint que usa el selector de eventos del
 * Panel. `pk` es el id de Dashbo de la cuenta (el `key` de la propuesta). La primera vez puede tardar
 * unos segundos: si la cuenta no tiene acciones guardadas, el backend se las pide a la plataforma.
 */
export function fetchAccountActions(pk) {
    return new APIClient(`${API_URL}agency/ad-accounts/${pk}/actions`).call('GET');
}

export function postAddBulk(items) {
    return new APIClient(`${API_URL}business-unit/add-bulk`).call('POST', items);
}

/**
 * Los clientes que la agencia YA tiene.
 *
 * Hace falta para asignar a mano una cuenta huérfana: sin esta lista, el único destino que la
 * pantalla puede ofrecer es crear un cliente nuevo, y si el usuario escribe el nombre de uno que ya
 * existe el alta rebota con "ese nombre está tomado" sobre algo que él ve correcto.
 */
export function fetchAgencyClients() {
    return new APIClient(`${API_URL}agency/clients/`).call('GET', {}, {cache: true});
}

/**
 * Los datos generales de la agencia: hoy, si puede elegir la moneda de una línea.
 *
 * Cambiar la moneda de una línea de control es un módulo de la suscripción
 * (`agency_has_module_multi_currency`), no algo que toda agencia pueda hacer. El paso 4 pregunta lo
 * mismo que pregunta el alta de una línea a mano, y por el mismo endpoint: ofrecer el selector sin
 * consultarlo dejaría al usuario eligiendo una moneda que después no va a poder volver a cambiar.
 */
export function fetchAgencyData() {
    return new APIClient(`${API_URL}get-agency-data`).call('GET', {}, {cache: true});
}

export function fetchConnections() {
    return new APIClient(`${API_URL}agency/connections/`).call('GET');
}

/**
 * El estado de la puesta en marcha.
 *
 * Con cache porque la tarjeta del menú lo pide en CADA navegación —el menú se monta dentro de cada
 * pantalla— y sin esto sería un pedido por click para toda agencia de la experiencia nueva. El TTL
 * corto alcanza: el estado cambia por acciones del propio usuario, que además refrescan a mano.
 */
export function fetchOnboardingStatus({cache = true} = {}) {
    return new APIClient(`${API_URL}agency/onboarding/status/`).call('GET', {}, {cache});
}

/**
 * Marcar y desmarcar un paso a mano.
 *
 * Los dos invalidan la entrada cacheada del estado. Pedirlo `{cache: false}` NO alcanza: esa opción
 * saltea la lectura pero **no reescribe** lo que ya estaba guardado, así que el pedido siguiente que
 * sí usa el cache —la tarjeta del menú, que se monta en cada navegación— servía la respuesta anterior
 * al click y el paso volvía a verse pendiente durante los 30 s del TTL.
 */
export function completeOnboardingStep(step) {
    return new APIClient(`${API_URL}agency/onboarding/steps/${step}/complete/`).call('POST', {})
        .then(forgetOnboardingStatusCache);
}

export function uncompleteOnboardingStep(step) {
    return new APIClient(`${API_URL}agency/onboarding/steps/${step}/uncomplete/`).call('POST', {})
        .then(forgetOnboardingStatusCache);
}

/**
 * Olvidar la copia cacheada del estado.
 *
 * Se exporta además de usarse acá porque hay un segundo momento en que el estado cambia sin pasar
 * por estos POST: cuando el túnel **detecta** la primera consulta. `{cache: false}` saltea la
 * lectura pero no reescribe lo guardado, así que la tarjeta del menú —que pide con `cache: true`—
 * puede seguir mostrando el paso como pendiente durante 30 s justo después de celebrarlo.
 */
export function forgetOnboardingStatusCache(response) {
    forgetOnboardingStatus();
    return response;
}

/**
 * Si los datos de la agencia ya son consultables por su IA.
 *
 * Lo poll-ea el cierre del tunel mientras el usuario conecta su asistente: una agencia nueva nace
 * sin datos sincronizados, y si pregunta antes de tiempo la IA le contesta vacio justo en el
 * momento en que decide si Dashbo le sirve.
 *
 * Sin cache, a diferencia del estado de la puesta en marcha: esto es exactamente lo que cambia
 * mientras la pantalla mira, y los 30 s de TTL mostrarian un contador congelado.
 */
export function fetchDataReadiness() {
    return new APIClient(`${API_URL}agency/onboarding/data-readiness/`).call('GET', {}, {cache: false});
}

/**
 * En qué va cada línea que acaba de crear el alta: su cliente, su cuenta y el paso en curso.
 *
 * Sin ids devuelve las líneas del alta masiva recientes de la agencia: es lo que se usa al volver
 * después de «Guardar y seguir después», cuando los ids de la respuesta del POST se perdieron.
 * Sin cache por lo mismo que `fetchDataReadiness`: es exactamente lo que cambia mientras se mira.
 */
export function fetchApplyProgress(businessUnitIds) {
    const query = businessUnitIds && businessUnitIds.length ? `?business_unit_ids=${businessUnitIds.join(',')}` : '';
    return new APIClient(`${API_URL}agency/onboarding/apply-progress/${query}`).call('GET', {}, {cache: false});
}

export function fetchRealtimeConfig() {
    return new APIClient(`${API_URL}realtime/config/`).call('GET');
}

export function fetchAuthUrlFor(channelPath) {
    return new APIClient(`${API_URL}get_auth_url/${channelPath}`).call('GET');
}

function chunk(items, size) {
    const batches = [];
    for (let index = 0; index < items.length; index += size) {
        batches.push(items.slice(index, index + size));
    }
    return batches;
}

/**
 * Junta las tandas en una sola respuesta, sin perder lo que cada una dijo.
 *
 * Dos cosas que antes se perdían y hacían mentir a la pantalla:
 *
 * - **La tanda que falló.** Con 400 cuentas son dos pedidos: si el segundo se caía, el usuario veía
 *   200 presupuestos y 200 filas vacías, sin ningún aviso y sin forma de distinguirlo de "esas
 *   cuentas no tienen gasto". Ahora la respuesta lo dice (`partial`) y el estado deja de ser 'OK'.
 * - **`refresh`**, que es lo que indica si el catálogo se está trayendo todavía. Se combina con OR
 *   —basta que una tanda siga preparándose— y con el mayor de los tiempos de reintento.
 */
function mergeBudgetResponses(responses) {
    const budgets = {};
    let period = null;
    let pending = false;
    let retryAfter = 0;
    let failed = 0;

    responses.forEach(response => {
        if (response?.status !== 'OK') {
            failed += 1;
            return;
        }
        Object.assign(budgets, response.data?.budgets || {});
        period = period || response.data?.period || null;
        const refresh = response.data?.refresh;
        if (refresh?.pending) pending = true;
        retryAfter = Math.max(retryAfter, Number(refresh?.retry_after_seconds) || 0);
    });

    const everythingFailed = failed > 0 && failed === responses.length;
    return {
        status: everythingFailed ? 'ERROR' : 'OK',
        partial: failed > 0 && !everythingFailed,
        data: {budgets, period, refresh: {pending, retry_after_seconds: retryAfter || null}},
    };
}
